import { useState, useRef, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import {
  X, Download, ExternalLink, Loader2, FileText, Maximize2,
  Sparkles, Send, MessageSquare,
} from 'lucide-react'
import { apiGet } from '@/lib/api'
import { cn } from '@/lib/utils'
import Markdown from 'react-markdown'

interface DocumentViewerProps {
  docId: string
  title: string
  onClose: () => void
}

interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

export function DocumentViewer({ docId, title, onClose }: DocumentViewerProps) {
  const [fullscreen, setFullscreen] = useState(false)
  const [showChat, setShowChat] = useState(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [streaming, setStreaming] = useState(false)
  const [followUps, setFollowUps] = useState<string[]>([])
  const chatEndRef = useRef<HTMLDivElement>(null)

  const stored = localStorage.getItem('estatekey-auth')
  const mkToken = stored ? JSON.parse(stored)?.state?.accessToken ?? '' : ''
  const pdfProxyUrl = `/api/v1/docflow/documents/${docId}/pdf?token=${encodeURIComponent(mkToken)}`
  const downloadProxyUrl = `/api/v1/docflow/documents/${docId}/download-proxy?token=${encodeURIComponent(mkToken)}`

  const { data: docDetail } = useQuery({
    queryKey: ['docflow-doc', docId],
    queryFn: () => apiGet<any>(`/docflow/documents/${docId}`),
  })

  const isPdf = docDetail?.mime_type === 'application/pdf' || docDetail?.has_pdf_rendition
  const isOfficeDoc = docDetail?.mime_type?.includes('word') || docDetail?.mime_type?.includes('spreadsheet') || docDetail?.mime_type?.includes('presentation') || docDetail?.filename?.match(/\.(docx?|xlsx?|pptx?)$/i)
  const pdfUrl = isPdf ? pdfProxyUrl : null
  const downloadUrl = downloadProxyUrl
  const hasText = docDetail?.has_text_content

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  async function handleChat(question?: string) {
    const msg = question ?? input.trim()
    if (!msg || streaming) return
    setInput('')
    setShowChat(true)
    setFollowUps([])

    setMessages(prev => [...prev, { role: 'user', content: msg }])
    setMessages(prev => [...prev, { role: 'assistant', content: '' }])
    setStreaming(true)

    try {
      const token = JSON.parse(localStorage.getItem('estatekey-auth') ?? '{}')?.state?.accessToken
      const response = await fetch('/api/v1/docflow/ai/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          documentIds: [docId],
          message: msg,
          history: messages.slice(-10).map(m => ({ role: m.role, content: m.content })),
        }),
      })

      if (!response.ok) {
        setMessages(prev => {
          const updated = [...prev]
          updated[updated.length - 1] = { role: 'assistant', content: 'Sorry, AI chat is not available for this document. It may not have been processed yet.' }
          return updated
        })
        setStreaming(false)
        return
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No reader')
      const decoder = new TextDecoder()

      while (true) {
        const { done, value } = await reader.read()
        if (done) break
        const chunk = decoder.decode(value, { stream: true })
        const lines = chunk.split('\n')
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          try {
            const data = JSON.parse(line.slice(6))
            if (data.done) {
              if (data.follow_ups?.length) setFollowUps(data.follow_ups)
              break
            }
            if (data.token) {
              setMessages(prev => {
                const updated = [...prev]
                const last = updated[updated.length - 1]
                updated[updated.length - 1] = { ...last, content: last.content + data.token }
                return updated
              })
            }
          } catch { /* skip */ }
        }
      }
    } catch {
      setMessages(prev => {
        const updated = [...prev]
        updated[updated.length - 1] = { role: 'assistant', content: 'Failed to connect to AI service.' }
        return updated
      })
    } finally {
      setStreaming(false)
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleChat()
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={e => e.stopPropagation()}>
      <div className={cn(
        'bg-white rounded-xl shadow-2xl flex flex-col',
        fullscreen ? 'w-full h-full rounded-none' : 'max-w-5xl w-full mx-4 max-h-[90vh]'
      )}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FileText className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <h2 className="text-sm font-semibold text-navy-800 truncate">{title}</h2>
          </div>
          <div className="flex items-center gap-1">
            {hasText && (
              <button
                onClick={() => setShowChat(!showChat)}
                className={cn(
                  'p-2 rounded-lg transition-colors',
                  showChat ? 'bg-purple-100 text-purple-700' : 'text-gray-400 hover:text-navy-800 hover:bg-gray-100'
                )}
                title="Ask AI about this document"
              >
                <Sparkles className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => window.open(downloadUrl, '_blank')}
              className="p-2 text-gray-400 hover:text-navy-800 rounded-lg hover:bg-gray-100 transition-colors"
              title="Download"
              type="button"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              onClick={() => setFullscreen(!fullscreen)}
              className="p-2 text-gray-400 hover:text-navy-800 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-navy-800 rounded-lg hover:bg-gray-100 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Content area — split view when chat is open */}
        <div className="flex-1 flex overflow-hidden">
          {/* Document view */}
          <div className={cn('flex-1 overflow-hidden bg-gray-100', showChat && 'border-r border-gray-200')}>
            {!docDetail ? (
              <div className="flex items-center justify-center h-full min-h-[400px]">
                <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
              </div>
            ) : pdfUrl ? (
              <iframe src={pdfUrl} className="w-full h-full min-h-[500px]" title={title} />
            ) : (
              <div className="flex flex-col items-center justify-center h-full min-h-[400px] gap-4">
                <FileText className="w-16 h-16 text-gray-300" />
                <div className="text-center">
                  <p className="text-sm font-medium text-navy-800">{docDetail?.filename}</p>
                  <p className="text-xs text-gray-400 mt-1">{docDetail?.mime_type}</p>
                </div>
                <button onClick={() => window.open(downloadUrl, '_blank')} className="btn-primary">
                  <Download className="w-4 h-4" /> Download
                </button>
              </div>
            )}
          </div>

          {/* AI Chat panel */}
          {showChat && (
            <div className="w-80 flex flex-col bg-white flex-shrink-0" onClick={e => e.stopPropagation()}>
              {/* Chat header */}
              <div className="px-3 py-2 border-b border-gray-100 bg-purple-50">
                <p className="text-xs font-semibold text-purple-800 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> Ask about this document
                </p>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {messages.length === 0 && (
                  <div className="text-center py-6">
                    <MessageSquare className="w-8 h-8 text-gray-200 mx-auto mb-2" />
                    <p className="text-xs text-gray-400">Ask a question about this document</p>
                    <div className="mt-3 space-y-1.5">
                      {[
                        'Summarise this document',
                        'What are the key findings?',
                        'When does this expire?',
                        'Are there any issues flagged?',
                      ].map(q => (
                        <div
                          key={q}
                          role="button"
                          onMouseDown={(e) => { e.preventDefault(); e.stopPropagation() }}
                          onClickCapture={(e) => { e.preventDefault(); e.stopPropagation(); handleChat(q) }}
                          className="block w-full text-left text-xs text-navy-600 bg-gray-50 hover:bg-gray-100 rounded-lg px-2.5 py-1.5 transition-colors cursor-pointer select-none"
                        >
                          {q}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {messages.map((msg, i) => (
                  <div key={i} className={cn('flex gap-2', msg.role === 'user' ? 'justify-end' : 'justify-start')}>
                    {msg.role === 'assistant' && (
                      <div className="w-5 h-5 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Sparkles className="w-3 h-3 text-purple-600" />
                      </div>
                    )}
                    <div className={cn(
                      'rounded-lg px-2.5 py-1.5 max-w-[240px] text-xs',
                      msg.role === 'user' ? 'bg-navy-800 text-white' : 'bg-gray-100 text-gray-800'
                    )}>
                      {msg.role === 'assistant' ? (
                        msg.content ? (
                          <div className="prose prose-xs max-w-none [&>p]:my-1 [&>ul]:my-1 [&>ol]:my-1 [&>li]:my-0.5 [&>h1]:text-sm [&>h2]:text-xs [&>h3]:text-xs [&>h1]:font-semibold [&>h2]:font-semibold [&>h3]:font-semibold [&>p]:text-xs [&>ul]:text-xs [&>ol]:text-xs [&_code]:text-[10px] [&_code]:bg-gray-200 [&_code]:px-1 [&_code]:rounded [&>blockquote]:border-l-2 [&>blockquote]:border-purple-300 [&>blockquote]:pl-2 [&>blockquote]:text-xs [&>blockquote]:text-gray-500">
                            <Markdown components={{ a: ({ children }) => <span className="text-purple-600 font-medium">{children}</span> }}>
                              {msg.content}
                            </Markdown>
                          </div>
                        ) : streaming && i === messages.length - 1 ? (
                          <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
                        ) : null
                      ) : (
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}

                {/* Follow-up suggestions */}
                {followUps.length > 0 && !streaming && (
                  <div className="space-y-1">
                    {followUps.map((q, i) => (
                      <div
                        key={i}
                        role="button"
                        onMouseDown={(e) => { e.preventDefault(); e.stopPropagation() }}
                        onClickCapture={(e) => { e.preventDefault(); e.stopPropagation(); handleChat(q) }}
                        className="block w-full text-left text-xs text-purple-600 bg-purple-50 hover:bg-purple-100 rounded-lg px-2.5 py-1.5 transition-colors cursor-pointer select-none"
                      >
                        {q}
                      </div>
                    ))}
                  </div>
                )}

                <div ref={chatEndRef} />
              </div>

              {/* Input */}
              <div className="p-2 border-t border-gray-100">
                <div className="flex gap-1.5">
                  <input
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask a question..."
                    className="input text-xs flex-1 py-1.5"
                    disabled={streaming}
                  />
                  <button
                    onClick={() => handleChat()}
                    disabled={!input.trim() || streaming}
                    className="btn-primary px-2 py-1.5"
                  >
                    {streaming ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {docDetail && (
          <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-4 text-xs text-gray-400 flex-shrink-0">
            <span>{docDetail.filename}</span>
            <span>{docDetail.mime_type}</span>
            {docDetail.file_size && <span>{(docDetail.file_size / 1024).toFixed(0)} KB</span>}
            {docDetail.current_version_label && <span>v{docDetail.current_version_label}</span>}
            {docDetail.owner_name && <span>Owner: {docDetail.owner_name}</span>}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
